// The offer card's decision (#1848 tickets 04, 18): whether a surface shows
// the tutorial offer, its resume form, or nothing. Pure, so the edge cases
// (an unloaded project reads as Film, `?fountain=` against the project type,
// snooze, dismissals, the post-auth marker) are tested without the app.
//
// Three surfaces show the one card: a Film Editor, the projects list, and
// whatever screen the user lands on right after sign-up or login. They share
// `user.tutorial`; only the Editor adds a gate of its own.

import type { IProject, TutorialUnit, UserTutorial, UserTutorialPatch } from '@/types';
import { isMovieProject } from '@/utils/projectHelpers';

/** «Позже» this many times is «Не показывать». */
const MAX_DISMISSALS = 3;

/** Which surface shows the card — sent with the offer events. */
export type OfferSurface = 'editor' | 'projects' | 'post-auth';

/** Where the card would mount: the Editor, the projects list, or any other screen. */
export type OfferPlace = 'editor' | 'projects' | 'other';

/** The surface that owns the card here, if any. Right after sign-up or login
 *  it is the post-auth one wherever the user landed, the Editor included. */
export function offerSurface(place: OfferPlace, postAuth: boolean): OfferSurface | null {
    if (postAuth) return 'post-auth';
    if (place === 'other') return null;
    return place;
}

/** The Editor's own gate: a loaded Film project that renders Fountain.
 *  `undefined` while the project loads — `isMovieProject` says Film for it. */
export function editorOffersTutorial(project: Pick<IProject, 'type' | 'episodes_qty'> | undefined, rendersFountain: boolean) {
    if (project === undefined) return undefined;
    // A `?fountain=` override that disagrees with the project type: nothing
    // shows (and nothing counts as seen).
    return isMovieProject(project) && rendersFountain;
}

/** Only asked with the tutorial on (`useTutorialEnabled`): the flag gates the
 *  card from outside. */
export interface OfferInput {
    /** The project of the Editor on screen; `null` off the Editor. */
    projectId: number | null;
    /** `undefined` until it has loaded. */
    tutorial: UserTutorial | undefined;
    activeUnit: TutorialUnit | null;
    /** The offer was answered in this page already, on any surface: the write
     *  may not have landed yet. */
    answered: boolean;
    now: Date;
}

export type OfferDecision =
    | { kind: 'none' }
    | { kind: 'offer' }
    /** `projectId`: the tutorial project the unit resumes in. */
    | { kind: 'resume'; unit: TutorialUnit; projectId: number };

const NONE: OfferDecision = { kind: 'none' };

/** The same on every surface: only the Editor adds a gate, from outside. */
export function decideTutorialOffer({ projectId, tutorial, activeUnit, answered, now }: OfferInput): OfferDecision {
    if (tutorial === undefined) return NONE;

    if (activeUnit !== null && tutorial.project_id !== null) {
        // In the tutorial project the run resumes by itself, with no prompt.
        return tutorial.project_id === projectId ? NONE : { kind: 'resume', unit: activeUnit, projectId: tutorial.project_id };
    }

    if (answered || tutorial.core !== null) return NONE;
    if (tutorial.offer.dismiss_count >= MAX_DISMISSALS) return NONE;
    const snoozedUntil = tutorial.offer.snoozed_until === null ? Number.NaN : Date.parse(tutorial.offer.snoozed_until);
    if (now.getTime() < snoozedUntil) return NONE;
    return { kind: 'offer' };
}

/** Set at sign-up and login. `shownAt`: the screen the post-auth card first
 *  showed on — leaving it ends the post-auth offer. */
export interface PostAuthMarker {
    shownAt: string | null;
}

export type PostAuthEvent =
    /** The card is on screen at `pathname`. */
    | { kind: 'shown'; pathname: string }
    /** A surface is mounted at `pathname`. */
    | { kind: 'route'; pathname: string }
    /** The decision settled on nothing: the user is not offered it after this login. */
    | { kind: 'none' }
    | { kind: 'answered' };

/** Whether the marker still holds at `pathname` — read during render, so a
 *  card mounting on a new screen never takes the post-auth surface for one
 *  commit before the `route` event ends it. */
export function postAuthHolds(marker: PostAuthMarker | null, pathname: string) {
    return nextPostAuthMarker(marker, { kind: 'route', pathname }) !== null;
}

export function nextPostAuthMarker(marker: PostAuthMarker | null, event: PostAuthEvent): PostAuthMarker | null {
    if (marker === null) return null;
    switch (event.kind) {
        case 'shown':
            return marker.shownAt === null ? { shownAt: event.pathname } : marker;
        case 'route':
            return marker.shownAt === null || marker.shownAt === event.pathname ? marker : null;
        case 'none':
        case 'answered':
            return null;
    }
}

/** «Позже»: away until the start of the next day. The third time it is
 *  «Не показывать». */
export function offerAfterLater(offer: UserTutorial['offer'], now: Date): UserTutorialPatch {
    const dismissCount = offer.dismiss_count + 1;
    if (dismissCount >= MAX_DISMISSALS) return { core: 'skipped', offer: { snoozed_until: null, dismiss_count: dismissCount } };
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return { offer: { snoozed_until: tomorrow.toISOString(), dismiss_count: dismissCount } };
}
